import { useEffect, useRef } from 'react';

type IconProps = { className?: string };

export function LogoMark({ className = '' }: IconProps) {
  return (
    <span className={`logo-mark ${className}`.trim()} aria-hidden>
      <img src="/logo.png" alt="" width={28} height={28} />
    </span>
  );
}

export function AnimatedLogoMark({ className = '' }: IconProps) {
  const shellRef = useRef<HTMLSpanElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const greetingRef = useRef(false);
  const mirroredRef = useRef(false);
  const rafRef = useRef(0);
  const lastPointerXRef = useRef(
    typeof window !== 'undefined' ? window.innerWidth / 2 : 0
  );

  useEffect(() => {
    const shell = shellRef.current;
    const img = imgRef.current;
    if (!shell || !img) return;

    const setMirror = (mirror: boolean) => {
      if (mirroredRef.current === mirror) return;
      mirroredRef.current = mirror;
      img.style.transform = mirror ? 'scaleX(-1)' : 'scaleX(1)';
    };

    const lookFromPointer = (clientX: number) => {
      if (greetingRef.current) return;
      const rect = shell.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      setMirror(clientX < cx);
    };

    const playGreet = () => {
      if (greetingRef.current) return;
      greetingRef.current = true;
      shell.classList.add('logo-mark--greet');
      setMirror(false);
      window.setTimeout(() => {
        shell.classList.remove('logo-mark--greet');
        greetingRef.current = false;
        lookFromPointer(lastPointerXRef.current);
      }, 2600);
    };

    const onMove = (e: MouseEvent) => {
      lastPointerXRef.current = e.clientX;
      if (rafRef.current) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = 0;
        lookFromPointer(e.clientX);
      });
    };

    document.addEventListener('mousemove', onMove, { passive: true });

    const initialTimer = window.setTimeout(playGreet, 2800);
    let greetTimer = 0;

    const scheduleGreet = () => {
      const delay = 10000 + Math.random() * 8000;
      greetTimer = window.setTimeout(() => {
        playGreet();
        scheduleGreet();
      }, delay);
    };
    scheduleGreet();

    return () => {
      document.removeEventListener('mousemove', onMove);
      window.clearTimeout(initialTimer);
      window.clearTimeout(greetTimer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <span className={`logo-mark logo-mark--animated ${className}`.trim()} aria-hidden>
      <span ref={shellRef} className="logo-mark__shell">
        <span className="logo-mark__wrap">
          <img ref={imgRef} src="/logo.png" alt="" width={48} height={48} />
        </span>
      </span>
    </span>
  );
}

const paths: Record<string, string> = {
  agent: 'M12 4a4 4 0 100 8 4 4 0 000-8zm-8 18v-1a6 6 0 016-6h4a6 6 0 016 6v1',
  channels: 'M8 12h8M8 16h12M8 20h6M4 6h16v16H4z',
  inbox: 'M4 6h16l2 4v10H4V6zm2 4l2 2h8l2-2',
  flow: 'M6 8h4v4H6zm10 0h4v4h-4zM6 18h4v4H6zm4-6h4v4h-4z',
  learn: 'M12 4l8 4v8l-8 4-8-4V8l8-4z',
  calendar: 'M6 4v4H4v16h16V8h-2V4H6zm2 4h12v12H8V8z',
  bell: 'M12 4a4 4 0 014 4v2l2 2v2H6v-2l2-2V8a4 4 0 014-4zM10 22h4',
  mobile: 'M8 4h8v20H8V4zm2 6h4v8h-4z',
  shield: 'M12 4l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V7l8-3z',
  plugin: 'M8 8h8v4H8V8zm0 8h8v4H8v-4z',
  demo: 'M6 8l10 6-10 6V8z',
  chart: 'M6 20V12M12 20V8M18 20v-6',
};

export function FeatureIcon({ name, className = '' }: { name: string; className?: string }) {
  const d = paths[name] ?? paths.agent;
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}
